import { useInView } from '../../hooks/useInView';

interface AreasProps {
  eyebrow?: string;
  headline: string;
  description: string;
  areas: string[];
  ctaText?: string;
  ctaHref?: string;
  showMap?: boolean;
}

export default function SectionAreas({
  eyebrow = 'Where We Dive',
  headline,
  description,
  areas,
  ctaText = 'Check Availability Near You',
  ctaHref = '#contact',
  showMap = false,
}: AreasProps) {
  const { ref, inView } = useInView();

  return (
    <section id="service-areas" className="relative py-28 md:py-36 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3265c2ac1720cf7511c3db82908e7503.jpg"
          alt="Florida Keys and Miami aerial diving locations"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0A1628]/82" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/40 via-transparent to-[#0A1628]/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {showMap ? (
          /* Two-column with map */
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            <div
              ref={ref as React.RefObject<HTMLDivElement>}
              className={`lg:w-1/2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-[#00B4D8]" />
                <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">{eyebrow}</span>
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {headline}
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">{description}</p>

              <div className="flex flex-wrap gap-2.5 mb-10">
                {areas.map((area) => (
                  <span
                    key={area}
                    className="bg-white/10 hover:bg-[#00B4D8]/25 border border-white/20 text-white text-[13px] px-4 py-2 rounded-full transition-colors duration-200 cursor-pointer"
                  >
                    {area}
                  </span>
                ))}
              </div>

              <a
                href={ctaHref}
                className="inline-flex items-center gap-2.5 bg-[#00B4D8] hover:bg-[#009FBF] text-white font-semibold px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-[15px]"
              >
                {ctaText}
                <i className="ri-map-pin-line text-base" />
              </a>
            </div>

            {/* Map */}
            <div className="lg:w-1/2 w-full">
              <div className="w-full h-[380px] rounded-2xl overflow-hidden border border-white/15">
                <iframe
                  title="Ace Diving Miami Location"
                  src="https://maps.google.com/maps?q=401+Biscayne+Blvd+Miami+FL+33132&output=embed&z=15"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-map-pin-2-line text-[#00B4D8] text-base" />
                </div>
                <p className="text-white/55 text-sm leading-relaxed">
                  401 Biscayne Boulevard, Pier 5, Slip 33<br />
                  Miami, Florida 33132
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Centered layout */
          <div
            ref={ref as React.RefObject<HTMLDivElement>}
            className={`max-w-3xl mx-auto text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#00B4D8]" />
              <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">{eyebrow}</span>
              <div className="h-px w-10 bg-[#00B4D8]" />
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-10">{description}</p>

            <div className="flex flex-wrap justify-center gap-2.5 mb-12">
              {areas.map((area) => (
                <span
                  key={area}
                  className="bg-white/10 hover:bg-[#00B4D8]/25 border border-white/20 text-white text-[13px] px-5 py-2 rounded-full transition-colors duration-200 cursor-pointer"
                >
                  {area}
                </span>
              ))}
            </div>

            <a
              href={ctaHref}
              className="inline-flex items-center gap-2.5 bg-[#00B4D8] hover:bg-[#009FBF] text-white font-semibold px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-[15px]"
            >
              {ctaText}
              <i className="ri-map-pin-line text-base" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
